import { type FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import PortableWifiOffIcon from "@mui/icons-material/PortableWifiOff";
import { Player } from "@/shared/components/Player";
import type { Stream } from "@/partner/entities/streams";
import {
  StreamSignalStateType,
  StreamStatusType,
} from "@/types/graphql/partner.generated";
import { StreamRecordingTrimStatus } from "./StreamRecordingTrimStatus";

interface StreamPlayerPreviewProps {
  stream: Stream;
  /** Обложка контента — фон плашки «нет сигнала» (референсный
   * PlayerPreviewInner показывает previewUrl под ErrorScreen). */
  posterUrl?: string | null;
}

// Три состояния (референсный Broadcast/PlayerPreviewInner + запись эфира):
// сигнал есть — плеер стартует сам (autoPlay muted: без mute браузер
// заблокирует автостарт); стрим завершён и запись готова — плеер той же
// записи, но без autoPlay (партнёр сам решает, когда смотреть готовую
// запись); иначе — плашка 16:9 с обложкой и иконкой ("нет сигнала" до
// завершения, "запись обрабатывается" после). liveConnected реактивно
// обновляется подпиской useContentStreamStatusChangedSubscription
// (EditStreamDialog), поэтому при подаче потока превью оживает само.
// Подписка может пропустить событие (Ring C 24.10: превью залипло в «Нет
// сигнала» после переподключения), поэтому живым считается и эфир, у
// которого проверка сигнала видит видео (signal.state OK), а диалог
// перезапрашивает стрим раз в минуту и при переподключении сокета.
export const StreamPlayerPreview: FC<StreamPlayerPreviewProps> = (props) => (
  <>
    <StreamPlayerPreviewInner {...props} />
    {/* Под плеером или плашкой: статус обрезки записи до окна эфира. */}
    <StreamRecordingTrimStatus stream={props.stream} sx={{ px: 2, py: 1.5 }} />
  </>
);

const StreamPlayerPreviewInner: FC<StreamPlayerPreviewProps> = ({
  stream,
  posterUrl,
}) => {
  const { t } = useTranslation();

  const live =
    stream.liveConnected ||
    (stream.status === StreamStatusType.StartedType &&
      stream.signal?.state === StreamSignalStateType.OkType);

  if (live && stream.playback?.hlsUrl) {
    return (
      <Player
        src={stream.playback.hlsUrl}
        poster={stream.playback.thumbnailUrl ?? undefined}
        autoPlay
        muted
        hotkeys={false}
      />
    );
  }

  if (stream.status === "FINISHED" && stream.recordPlayback?.hlsUrl) {
    return (
      <Player
        src={stream.recordPlayback.hlsUrl}
        poster={stream.recordPlayback.thumbnailUrl ?? undefined}
        muted
        hotkeys={false}
      />
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        pt: "56%",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#090909",
        ...(posterUrl && {
          backgroundImage: `url(${posterUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }),
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          color: "common.white",
          // Поверх обложки — лёгкий скрим, чтобы иконка и текст читались
          ...(posterUrl && { bgcolor: "rgba(0, 0, 0, 0.55)" }),
        }}
      >
        <PortableWifiOffIcon fontSize="large" />
        <Typography variant="body2">
          {stream.status === "FINISHED" && stream.enableRecord
            ? t("streams.form.recordProcessing")
            : t("streams.form.noSignal")}
        </Typography>
      </Box>
    </Box>
  );
};
